import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { addExtras, updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormField, updateFields } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { CalculatorAmountContainer, CalculatorFrameTop } from 'apps/auth/components/common/Calculator/styles';
import { CalculatorInputAmount } from 'apps/auth/components/common/Calculator/subcomponents/CalculatorInputAmount';
import { RECALCULATE } from 'apps/auth/constants/ExtraNames';
import { AMOUNT_FROM, AMOUNT_FROM_CCY, AMOUNT_TO, AMOUNT_TO_CCY } from 'apps/auth/constants/fieldsName';
import update from 'apps/auth/helpers/Immutability';
import { timer } from 'apps/auth/helpers/utils';
import { useExtra } from 'apps/auth/hooks/selectors';
import { ApiService } from 'apps/auth/services/apiService';
import { FontSizeService } from 'apps/auth/services/fontSizeService';

const GaCalculator = (props) => {
    const timerId = useRef(null);
    const controllerRef = useRef(null);
    const [fontSize, setFontSize] = useState('');
    const fontSizeService = useMemo(() => new FontSizeService(), []);
    const { leftLimitAmount, recalculate, toCountry, userCountry } = useExtra();

    useEffect(() => {
        updateFontSize();
    }, []);

    const canCalculateCommission = (field) => field.value && !props.isShowSpinner && field.name === document.activeElement?.id;

    useEffect(() => {
        if (canCalculateCommission(props.amountFrom)) {
            timerId.current = timer(
                timerId.current,
                () => {
                    calcCommission(true);
                },
                500,
            );
        } else {
            updateFontSize();
            if (!props.amountFrom.value && props.amountTo.value) {
                updateAmountField(props.amountTo, 0, false, '', '', false);
            }
        }
    }, [props.amountFrom.value]);

    useEffect(() => {
        if (canCalculateCommission(props.amountTo)) {
            timerId.current = timer(
                timerId.current,
                () => {
                    calcCommission(false);
                },
                500,
            );
        } else {
            updateFontSize();
        }
    }, [props.amountTo.value]);

    useEffect(() => {
        if (recalculate === true) {
            calcCommission(true);
            props.updateExtraByName(RECALCULATE, false);
        }
    }, [recalculate]);

    useEffect(() => {
        if (!props.amountFrom.value && !props.amountTo.value) {
            return;
        }

        validateMinMaxMultiplePocketAmount(
            props.amountFrom,
            props.amountFrom.value,
            props.restrictionFrom.minAmount,
            props.restrictionFrom.maxAmount,
            props.restrictionFrom.multiple,
            true,
            props.currencyFrom.code,
        );
        validateMinMaxMultiplePocketAmount(
            props.amountTo,
            props.amountTo.value,
            props.restrictionTo.minAmount,
            props.restrictionTo.maxAmount,
            props.restrictionTo.multiple,
            false,
            props.currencyTo.code,
        );
    }, [props.amountFromCcy, props.amountToCcy]);

    const calcCommission = (isFrom) => {
        const params = new URLSearchParams({ isFrom });
        const url = `${props.fetchCommissionUrl}?${params.toString()}`;
        ApiService.fetchWithoutSpinner(
            url,
            props.fields,
            (json) => {
                if (json.messages) {
                    props.updatePageMessages(json.messages);
                }
                if (json.extra) {
                    props.addExtras(json.extra);
                    const commission = json.extra.commission;
                    if (commission) {
                        const fields = {};
                        if (isFrom) {
                            fields[AMOUNT_TO] = update(props.amountTo, { value: { $set: commission.to } });
                        } else {
                            fields[AMOUNT_FROM] = update(props.amountFrom, { value: { $set: commission.from } });
                        }
                        props.updateFields(props.formName, fields);
                    }
                }
            },
            controllerRef,
        );
    };

    const updateAmountField = (field, value, isError, errorCode, errorMessage, isShowErrorDetails) => {
        const newField = update(field, {
            $auto: {
                value: { $set: value },
                isError: { $set: isError },
                errorMessage: { $set: errorMessage },
                isShowErrorDetails: { $set: isShowErrorDetails },
                error: {
                    $auto: {
                        code: { $set: errorCode },
                        body: { $set: errorMessage },
                    },
                },
            },
        });
        props.changeFormField(props.formName, newField);
    };

    const validateAmountFrom = (value) => {
        return validateMinMaxMultiplePocketAmount(
            props.amountFrom,
            value,
            props.restrictionFrom.minAmount,
            !!leftLimitAmount ? leftLimitAmount : props.restrictionFrom.maxAmount,
            props.restrictionFrom.multiple,
            true,
            props.currencyFrom.code,
        );
    };

    const validateAmountTo = (value) => {
        return validateMinMaxMultiplePocketAmount(
            props.amountTo,
            value,
            props.restrictionTo.minAmount,
            props.restrictionTo.maxAmount,
            props.restrictionTo.multiple,
            false,
            props.currencyTo.code,
        );
    };

    const validateMinMaxMultiplePocketAmount = (field, value, min, max, multiple, isFrom, currencyCode) => {
        let error = props.getValidationError(value, min, max, multiple, isFrom, currencyCode);
        updateAmountField(field, value, error.isError, error.error?.code, error.errorMessage, error.isShowToViewDetails);
        return error.isShowToViewDetails;
    };

    const updateFontSize = () => {
        setFontSize(fontSizeService.calc(props.amountFrom.value, props.amountTo.value));
    };

    const decreaseAmountFrom = () => {
        let min = props.restrictionFrom.maxAmount;
        if (!!leftLimitAmount) {
            min = Math.min(leftLimitAmount, props.restrictionFrom.maxAmount);
        } else if (!!props.pocketBalance && !!props.restrictionFrom.maxAmount) {
            min = Math.min(props.pocketBalance, props.restrictionFrom.maxAmount);
        } else if (props.pocketBalance) {
            min = props.pocketBalance;
        }
        updateAmountField(props.amountFrom, min, false);
    };
    const decreaseAmountTo = () => {
        let currentValue = props.amountTo.value;
        let multiple = props.restrictionTo.multiple;
        let max = props.restrictionTo.maxAmount;
        let futureValue = max;

        if (currentValue < max && multiple && currentValue % multiple !== 0) {
            if (props.pocketBalance && props.amountFrom.value > props.pocketBalance) {
                // округляем в меньшую сторону, если юзер пытается перевести больше чем у него на балансе
                futureValue = Math.floor(currentValue / multiple) * multiple;
            } else {
                futureValue = Math.ceil(currentValue / multiple) * multiple;
            }
        }
        updateAmountField(props.amountTo, futureValue, false);
    };

    const onBlurHandlerAmountTo = () => {
        /*
проверяем поле from, так как pocketBalance в валюте from
 */
        if (+props.amountTo?.value) {
            validateAmountFrom(props.amountFrom?.value);
        }
    };
    const onBlurHandlerAmountFrom = () => {
        /*
при нажатии continue проверяем поле to на наличие ошибок
 */
        if (+props.amountFrom?.value) {
            validateAmountTo(props.amountTo?.value);
        }
    };
    return (
        <CalculatorFrameTop>
            <CalculatorAmountContainer>
                <CalculatorInputAmount
                    focus={true}
                    isGaFlow={true}
                    formName={props.formName}
                    amountField={props.amountFrom}
                    ccyField={props.amountFromCcy}
                    title={props.amountFromTitle}
                    restriction={props.restrictionFrom}
                    currencies={props.currenciesFrom}
                    customValidate={validateAmountFrom}
                    updateAmountField={updateAmountField}
                    customFontSize={fontSize}
                    currency={props.currencyFrom}
                    isSend={props.isSend}
                    isFrom={true}
                    onProcessLinkClick={decreaseAmountFrom}
                    decreaseAmountFrom={decreaseAmountFrom}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    pocketBalance={props.pocketBalance}
                    updateFontSize={updateFontSize}
                    onBlurHandler={onBlurHandlerAmountFrom}
                    isDouble
                    iconName={userCountry.code}
                    getCurrencyIcon={props.getCurrencyIcon}
                    onIconRenderError={props.onIconRenderError}
                />
                <CalculatorInputAmount
                    isGaFlow={true}
                    formName={props.formName}
                    amountField={props.amountTo}
                    ccyField={props.amountToCcy}
                    title={props.amountToTitle}
                    restriction={props.restrictionTo}
                    currencies={props.currenciesTo}
                    customValidate={validateAmountTo}
                    updateAmountField={updateAmountField}
                    customFontSize={fontSize}
                    currency={props.currencyTo}
                    isSend={props.isSend}
                    onProcessLinkClick={decreaseAmountTo}
                    decreaseAmountFrom={decreaseAmountFrom}
                    onSelectCurrencyClick={props.onSelectCurrencyClick}
                    displayArrows={!props.isSend}
                    updateFontSize={updateFontSize}
                    onBlurHandler={onBlurHandlerAmountTo}
                    isDouble
                    iconName={toCountry.code}
                    getCurrencyIcon={props.getCurrencyIcon}
                    onIconRenderError={props.onIconRenderError}
                />
            </CalculatorAmountContainer>
        </CalculatorFrameTop>
    );
};

GaCalculator.propTypes = {
    isShowSpinner: PropTypes.bool.isRequired,
    amountToTitle: PropTypes.string.isRequired,
    amountFromTitle: PropTypes.string.isRequired,
    pocketBalance: PropTypes.number,
    commission: PropTypes.object,
    fields: PropTypes.object.isRequired,
    amountFrom: PropTypes.object.isRequired,
    amountTo: PropTypes.object.isRequired,
    restrictionFrom: PropTypes.object,
    restrictionTo: PropTypes.object,
    fetchCommissionUrl: PropTypes.string.isRequired,
    isSend: PropTypes.bool,
    getValidationError: PropTypes.func.isRequired,
    onSelectCurrencyClick: PropTypes.func.isRequired,
    currencyTo: PropTypes.object,
    currencyFrom: PropTypes.object,
    currenciesFrom: PropTypes.array,
    currenciesTo: PropTypes.array,
    amountToCcy: PropTypes.object.isRequired,
    amountFromCcy: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    return {
        isShowSpinner: state.app.isShowSpinner,
        amountToTitle: state.messages.pageMessages.amountTo,
        amountFromTitle: state.messages.pageMessages.amountFrom,
        pocketBalance: state.extra?.pocket?.balance,
        commission: state.extra.commission,
        fields: state.form[ownProps.formName].fields,
        amountFrom: state.form[ownProps.formName].fields[AMOUNT_FROM],
        amountTo: state.form[ownProps.formName].fields[AMOUNT_TO],
        restrictionFrom: state.extra.commission?.restrictionFrom,
        restrictionTo: state.extra.commission?.restrictionTo,
        amountToCcy: state.form[ownProps.formName].fields[AMOUNT_TO_CCY],
        amountFromCcy: state.form[ownProps.formName].fields[AMOUNT_FROM_CCY],
    };
};

export default connect(mapStateToProps, {
    changeFormField,
    updatePageMessages,
    updateFields,
    addExtras,
    updateExtraByName,
})(GaCalculator);
